import { Db } from '@diamond/shared/schema';
import {
  GetServerSidePropsContext,
  GetServerSidePropsResult,
  PreviewData,
} from 'next';
import { ParsedUrlQuery } from 'querystring';

export type AuthData = {
  token: string;
  activeRole: Db['users']['role'];
  selectedShipToId: string;
};

type CustomGSSP<
  WithAuthData extends boolean,
  Props = Record<string, unknown>,
  Query extends ParsedUrlQuery = ParsedUrlQuery,
  Data extends PreviewData = PreviewData
> = WithAuthData extends true
  ? (
      ctx: GetServerSidePropsContext<Query, Data>,
      authData?: AuthData
    ) => Promise<GetServerSidePropsResult<Props>>
  : (
      ctx: GetServerSidePropsContext<Query, Data>
    ) => Promise<GetServerSidePropsResult<Props>>;

/**
 * It takes a function that returns a `GetServerSideProps` object, and returns a function that returns a `GetServerSideProps` object
 *
 * @param f - CustomGSSP<WithAuthData, Props, Query, Data>
 * @param {WithAuthData} [withAuthData] - If you want to get the user data from the
 * token, set this to true.
 *
 * @returns A function that takes a context and returns a promise that resolves to an object.
 */
export const withServerAuth =
  <
    Props = Record<string, unknown>,
    Query extends ParsedUrlQuery = ParsedUrlQuery,
    Data extends PreviewData = PreviewData,
    WithAuthData extends boolean = false
  >(
    f: CustomGSSP<WithAuthData, Props, Query, Data>,
    withAuthData?: WithAuthData
  ) =>
  async (ctx: GetServerSidePropsContext<Query, Data>) => {
    const token = ctx.req.cookies['buyer_jwt_token'];
    const activeRole = ctx.req.cookies[
      'buyer_active_role'
    ] as Db['users']['role'];
    const selectedShipToId = ctx.req.cookies['selected_shipto_id'] as string;

    if (!token) {
      return {
        redirect: {
          destination: '/login',
          permanent: false,
        },
      };
    }

    if (withAuthData) {
      return await f(ctx, { token, activeRole, selectedShipToId });
    }

    return await f(ctx);
  };
